import {
  globalMessageHandler,
  useDialog,
  useForm,
  Validator,
} from "common";
import { useAppSelector } from "app/hooks";
import settings from "app/settings";
import { selectAccessToken } from "features/identity/identitySlice";
import I18n from "i18n-js";
import { useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import inDev from "utils/inDev";
import {
  getIntegrationService,
  IntegrationFields,
  IntegrationFormItem,
  IntegrationOptions,
  IntegrationStatus,
  integrationTest,
  IntegrationType,
} from "./integrationApi";

export interface UseIntegrationFormProps {}

export default function useIntegrationForm() {
  const params = useParams<"integration_id">();
  const [searchParams] = useSearchParams();
  const typeFromUrlParams = searchParams.get("type");
  const accessToken = useAppSelector(selectAccessToken);
  const [testing, setTesting] = useState(false);
  const [historyOpen, setHistoryOpen, historyDialog] = useDialog();
  const service = getIntegrationService();
  const shouldConnectAfterSave = useRef(false);
  const fromRef = useRef<HTMLFormElement | null>(null);
  const navigate = useNavigate();

  const form = useForm<IntegrationFormItem, IntegrationOptions>({
    id: params.integration_id,
    translationCategory: "Integration",
    defaultValues: {
      filename_format: "{submit_datetime}",
      type: typeFromUrlParams as any,
      enabled: 1,
    },
    rules: {
      name: Validator.rule.any().required(),
      type: Validator.rule.any().required(),
      filename_format: Validator.rule.any().required(),
    },
    afterSave: item => {
      if (shouldConnectAfterSave.current) {
        shouldConnectAfterSave.current = false;
        oauthPost();
      }
    },
    afterDelete: item => {
      navigate("/integrations");
    },
    onValidationFailed: () => {
      globalMessageHandler.error(I18n.t("App.formHasErrors"));
    },
    ...service,
  });

  const hasConnect: boolean = [
    IntegrationType.GoogleDrive,
    IntegrationType.Dropbox,
    IntegrationType.OneDrive,
  ].includes(form.item.type);
  const connected: boolean = form.item.status === IntegrationStatus.Connected;
  const formAction: string = `${settings.apiHost}/integrations/connect/${form.item.id}?accessToken=${accessToken}`;
  async function oauthPost() {
    if (fromRef.current) {
      fromRef.current.submit();
    }
  }
  async function connect() {
    if (form.isDirty) {
      shouldConnectAfterSave.current = true;
      form.save();
    } else {
      oauthPost();
    }
  }
  async function save() {
    shouldConnectAfterSave.current = false;
    form.save();
  }
  async function disconnect() {
    inDev();
  }
  async function test() {
    setTesting(true);
    const success = await integrationTest(form.item?.id);
    setTesting(false);
    form.load(true);
    if (success) {
      globalMessageHandler.success(I18n.t("Integration.successTest"));
    } else {
      globalMessageHandler.error(I18n.t("Integration.failedTest"));
    }
  }

  const rules: { [key in IntegrationFields]: any } = {} as any;

  // if (form.item.type === IntegrationType.OneDrive) {
  //   rules.directory = Validator.rule.any().required();
  // }
  if (form.item.type === IntegrationType.Ftp) {
    rules.directory = Validator.rule.any().required();
    rules.host = Validator.rule.any().required();
    rules.port = Validator.rule.any().required();
    rules.username = Validator.rule.any().required();
    rules.password = Validator.rule.any().required();
  }
  if (
    [
      IntegrationType.Email,
      IntegrationType.MailChimp,
      IntegrationType.Smtp,
    ].includes(form.item.type)
  ) {
    rules.email = Validator.rule.any().required();
    rules.subject = Validator.rule.any().required();
    rules.message = Validator.rule.any().required();
  }
  if (form.item.type === IntegrationType.MailChimp) {
    rules.api_key = Validator.rule.any().required();
    rules.from_email = Validator.rule.any().required();
    rules.from_name = Validator.rule.any().required();
  }
  if (form.item.type === IntegrationType.Smtp) {
    rules.host = Validator.rule.any().required();
    //rules.port = Validator.rule.any().required();
    rules.username = Validator.rule.any().required();
    rules.password = Validator.rule.any().required();
    rules.from_email = Validator.rule.any().required();
    rules.from_name = Validator.rule.any().required();
  }
  form.addRules(rules);

  return {
    ...form,
    fromRef,
    formAction,
    save,
    connect,
    disconnect,
    test,
    hasConnect,
    connected,
    typeFromUrlParams,
    historyOpen,
    setHistoryOpen,
    historyDialog,
    testing,
  };
}
